import { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { useAppointmentId } from '@eluve/frontend-appointment-hooks';
import { useNamedLogger } from '@eluve/logger';
import { useTenantIdFromParams } from '@eluve/session-helpers';

/**
 * Customer-facing recovery action for an appointment whose note couldn't
 * be generated (failed transcription chunk, stuck LLM scheduling, prior
 * regenerate failure). Reschedules any failed transcripts and reseeds
 * summarization in one call. Idempotent — safe to call when nothing is
 * actually wrong.
 */
export const useRetryAppointmentProcessing = () => {
  const logger = useNamedLogger('useRetryAppointmentProcessing');
  const tenantId = useTenantIdFromParams();
  const appointmentId = useAppointmentId();
  const apiClient = useApiClient();

  const [isRetrying, setIsRetrying] = useState(false);

  const retryProcessing = async (
    onSuccess?: (result: {
      retriedTranscriptCount: number;
      summarizationSeeded: boolean;
    }) => void,
    onError?: (error: Error) => void,
  ) => {
    setIsRetrying(true);
    try {
      const result = await apiClient.appointments.retryProcessing({
        body: {},
        params: {
          tenantId: tenantId!,
          appointmentId,
        },
      });

      if (result.status !== 200) {
        throw new Error('Failed to retry appointment processing');
      }

      logger.info('Successfully triggered retry processing', {
        appointmentId,
        retriedTranscriptCount: result.body.retriedTranscriptCount,
      });
      onSuccess?.(result.body);
    } catch (error) {
      logger.error('Failed to retry appointment processing', {
        appointmentId,
        error,
      });
      onError?.(error as Error);
    } finally {
      setIsRetrying(false);
    }
  };

  return {
    retryProcessing,
    isRetrying,
  };
};
